import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { StyledFactory } from '../../mixins/Styled.js';
import { OneUxElement } from '../../OneUxElement.js';
import { Boundary } from '../../visualizations/common/mixins/Boundary.js';
import { Focusable } from '../../mixins/Focusable.js';
import { Label } from '../../mixins/Label.js';
import { log } from '../../utils/log.js';
import type { Optional } from '../../types.js';
import { style } from './style.js';
import { keyValue, keyLabel, series, size, yAxisRange, highlightState, yTarget } from './types.js';
import { Lang } from '../../visualizations/common/mixins/Lang.js';
import { ValueFormatter } from '../../visualizations/common/format.js';
import { getRenderGroups } from './render-groups/getRenderGroups.js';
import { KeyHoverPopout } from './axis/key/KeyHoverPopout.js';
import { MarginCalculator } from './MarginCalculator.js';
import { getKeyScale } from './axis/key/getKeyScale.js';
import { KeyValueSet } from './axis/key/KeyValueSet.js';
import { timing } from './animation.js';
import { getLanguage } from './lang.js';
import { until } from 'lit/directives/until.js';
import { prefersReducedMotion } from '../../utils/prefersReducedMotion.js';
import { getLegendsConfiguration } from './legend/getLegendsConfiguration.js';
import { ChartEmptyContent } from './fragments/ChartEmptyContent.js';
import { ChartContent } from './fragments/ChartContent.js';
const Styled = StyledFactory(style);
const BaseClass = Label(Lang(Focusable(Boundary(Styled(OneUxElement)))));
const legendsConfiguration = getLegendsConfiguration({
  isPopout: false
});
export class OneUxPlotChartElement extends BaseClass {
  static get elementType() {
    return 'one-ux-plot-chart';
  }
  constructor() {
    super();
    this.width = 'max';
  }

  /**
   * Defines the rendered keys in the x-axis. The type is an array of `string` | `number` | `{ key: string | number; label: string }`.
   * The key of a data point within a series will be plotted against the corresponding key.
   */
  @property({
    type: Array,
    attribute: 'keys'
  })
  public accessor keys!: (keyValue | keyLabel)[];
  @property({
    type: Array,
    attribute: 'series'
  })
  public accessor series: series[] = [];
  @property({
    type: Boolean,
    attribute: 'use-si-suffix'
  })
  public accessor useSISuffix = false;
  @property({
    type: Boolean,
    attribute: 'hide-supportlines'
  })
  public accessor hideSupportlines = false;
  @property({
    type: Boolean,
    attribute: 'hide-legends'
  })
  public accessor hideLegends = false;
  @property({
    type: Boolean,
    attribute: 'hide-axis-left'
  })
  public accessor hideAxisLeft = false;
  @property({
    type: Boolean,
    attribute: 'hide-axis-right'
  })
  public accessor hideAxisRight = false;
  @property({
    type: Object,
    attribute: 'axis-left-range'
  })
  public accessor axisLeftRange: Optional<yAxisRange>;
  @property({
    type: Object,
    attribute: 'axis-right-range'
  })
  public accessor axisRightRange: Optional<yAxisRange>;
  @property({
    type: Boolean,
    attribute: 'disable-point-label-collision'
  })
  public accessor disablePointLabelCollision = false;
  @property({
    type: Boolean,
    attribute: 'disable-animation'
  })
  public accessor disableAnimation = false;
  @state()
  private accessor _hoveredKeyBand: Optional<Element>;
  @state()
  private accessor _hoveredLegendSeries = '';
  #renderSvgInnerContentTimout: Optional<ReturnType<typeof setTimeout>>;
  protected render() {
    if (!this.keys) {
      log.error('Missing attribute keys, not rendering.');
      return;
    }
    if (!this.label) {
      log.deprecation('<one-ux-plot-chart> Missing attribute label, this element will stop working at some point in the near future if not provided.');
    }
    const {
      translations
    } = getLanguage(this);
    const valueFormatter = new ValueFormatter(this._locale, this.useSISuffix);
    const animate = !(this.disableAnimation || prefersReducedMotion());
    const renderSvgInnerContent = async () => {
      const svgSize = this.#getSvgSize();
      if (svgSize.width === 0 || svgSize.height === 0) {
        return nothing;
      }
      const delay = Number.isFinite(this.#renderSvgInnerContentTimout) ? 250 : 0;
      return new Promise(resolve => {
        clearTimeout(this.#renderSvgInnerContentTimout);
        this.#renderSvgInnerContentTimout = setTimeout(() => {
          return resolve(this.#SvgInnerContent(svgSize, valueFormatter, animate));
        }, delay);
      });
    };
    return html`<div
      class="one-ux-element--root"
      role="group"
      aria-roledescription=${translations.chart}
      aria-label=${this.label || translations.visualization}
      tabindex="0"
    >
      <svg style="width: 100%; flex: 1; overflow: visible; min-height: 0;" role="presentation">
        ${until(renderSvgInnerContent())}
      </svg>
      ${this._hoveredKeyBand ? KeyHoverPopout(this._hoveredKeyBand, this.series, valueFormatter) : nothing}
      <div
        class="slots"
        style=${styleMap({
      'animation-duration': `${timing(animate).duration}ms`
    })}
      >
        <slot class="slot-description one-ux-typography--label-300"></slot>
      </div>
    </div>`;
  }
  #SvgInnerContent(svgSize: size, valueFormatter: ValueFormatter, animate: boolean) {
    const keyValueSet = new KeyValueSet(this.keys);
    const renderGroupsMap = getRenderGroups(this.series, keyValueSet, this.axisLeftRange, this.axisRightRange);
    const svgMargin = new MarginCalculator(svgSize, valueFormatter, renderGroupsMap.left, renderGroupsMap.right, legendsConfiguration, (this.series ?? []).map(x => x.name), this.hideLegends, this.hideAxisLeft, this.hideAxisRight, keyValueSet, this.axisLeftRange, this.axisRightRange).calculate();
    const keyScale = getKeyScale(svgSize, svgMargin, keyValueSet);
    const highlightStateProvider = (seriesName: string): highlightState => {
      if (this._hoveredLegendSeries === '') return 'none';
      return seriesName === this._hoveredLegendSeries ? 'active' : 'inactive';
    };
    const highlightYTargetStateProvider = (yTarget: yTarget): highlightState => {
      if (this._hoveredLegendSeries === '') return 'none';
      const hoveredSeriesYTarget = this.series.find(s => s.name === this._hoveredLegendSeries)?.yTarget ?? 'left';
      return yTarget === hoveredSeriesYTarget ? 'active' : 'inactive';
    };
    const isAllSeriesEmpty = renderGroupsMap.all.length === 0;
    if (isAllSeriesEmpty) {
      return ChartEmptyContent.call(this, {
        legendsConfiguration,
        svgSize,
        animate,
        svgMargin,
        keyScale,
        highlightStateProvider
      });
    } else {
      return ChartContent.call(this, {
        legendsConfiguration,
        svgSize,
        animate,
        svgMargin,
        keyScale,
        renderGroupsMap,
        valueFormatter,
        highlightStateProvider,
        highlightYTargetStateProvider
      });
    }
  }
  protected _onKeyHoverBandMouseEnter = ($el: Element) => this._hoveredKeyBand = $el;
  protected _onKeyHoverBandMouseLeave = () => this._hoveredKeyBand = undefined;
  protected _onLegendMouseEnter = (seriesName: string) => this._hoveredLegendSeries = seriesName;
  protected _onLegendMouseLeave = () => this._hoveredLegendSeries = '';
  #getSvgSize(): size {
    const $svg = this.shadowRoot!.querySelector('svg');
    return {
      width: $svg ? $svg.clientWidth : 0,
      height: $svg ? $svg.clientHeight : 0
    };
  }
}